import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BellRing, PenLine, Video } from 'lucide-react';
import { BoutonSecondaire, Pastille } from '../ui/Primitives';
import { ChoixBloc } from '../emargement/ChoixBloc';
import { type SeanceAffichee, type PresenceAffichee } from '../../lib/useEmargement';
import { SignatureModal } from './SignatureModal';
import { ModaleSignatureFormateur } from '../emargement/ModaleSignatureFormateur';
import { LiensPv } from '../emargement/LiensPv';
import { texteRappel } from '../../lib/emargement-affichage';

function heure(valeur: string | null): string {
  if (!valeur) return '—';
  return new Date(valeur).toLocaleTimeString('fr-FR', {
    timeZone: 'Indian/Reunion',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const LIBELLES_SEANCE: Record<string, string> = {
  PREVUE: 'Prévue',
  EN_COURS: 'En cours',
  CLOTUREE: 'Clôturée',
};

export function FeuilleEmargement({
  seance,
  enCours,
  onRequalifier,
  onSigner,
  onSignerFormateur,
  erreurSignatureFormateur = null,
  onCloturer,
  onDetail,
}: {
  seance: SeanceAffichee;
  enCours: boolean;
  /**
   * Commandes réservées à qui tient la feuille. Absentes dans la vue de
   * l'apprenant, qui consulte sa demi-journée et y signe, sans requalifier
   * personne ni clôturer la séance.
   */
  onRequalifier?: (
    presenceId: string,
    statut: 'PRESENT' | 'RETARD' | 'ABSENT',
    justifiee?: boolean,
    motif?: string,
  ) => void;
  onSigner: (presenceId: string, trace: string) => void;
  /** En présentiel, le formateur signe d'un tracé, reproduit au PV. */
  onSignerFormateur?: (trace: string) => void;
  erreurSignatureFormateur?: string | null;
  onCloturer?: () => void;
  /**
   * Détail d'une ligne d'une feuille clôturée, où se demande sa correction à
   * la direction (TF-437). Absent dans la vue de l'apprenant.
   */
  onDetail?: (presenceId: string) => void;
}): JSX.Element {
  const [aSigner, setASigner] = useState<PresenceAffichee | null>(null);
  const [signatureFormateur, setSignatureFormateur] = useState(false);
  // La fenêtre se referme d'elle-même quand la signature est enregistrée.
  useEffect(() => {
    if (seance.formateurSigneLe) setSignatureFormateur(false);
  }, [seance.formateurSigneLe]);
  const cloturee = seance.statut === 'CLOTUREE';
  // Classe à distance : la tablette ne circule pas. L'apprenant émarge d'un
  // geste dans la salle, caméra allumée ; l'équipe régularise en requalifiant.
  const distance = seance.type === 'ELEARNING';

  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-card">
      <div className="gradient-card p-4 sm:p-6 text-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2">
              {!cloturee && <span className="pulse-ring h-2 w-2 rounded-full bg-codialis-mint" />}
              <span className="text-xs font-bold uppercase tracking-wider text-codialis-mint">
                {LIBELLES_SEANCE[seance.statut] ?? seance.statut}
              </span>
            </div>
            <h2 className="font-display text-2xl font-bold">{seance.libelle}</h2>
            <p className="mt-1 text-sm text-white/70">
              {seance.formation ?? 'Formation non précisée'} · {seance.siteNom}
            </p>
          </div>
          <div className="text-right">
            <div className="font-display text-3xl font-extrabold text-codialis-mint">
              {seance.compteurs.signatures} / {seance.compteurs.inscrits}
            </div>
            <div className="text-xs text-white/60">émargements recueillis</div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 border-t border-white/10 pt-6 md:grid-cols-4">
          {[
            ['Demi-journée', seance.demiJourneeLibelle],
            ['Horaires', `${seance.heureDebut} · ${seance.heureFin}`],
            ['Lieu', seance.lieu ?? (seance.type === 'ELEARNING' ? 'À distance' : '—')],
            ['Formateur', seance.formateur ?? 'Non affecté'],
          ].map(([libelle, valeur]) => (
            <div key={libelle}>
              <div className="text-xs uppercase text-white/60">{libelle}</div>
              <div className="font-semibold">{valeur}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {/* Le bloc se choisit par qui tient la feuille, pas par l'apprenant qui la signe. */}
        {onRequalifier && <ChoixBloc seance={seance} className="mb-4" />}
        {seance.rappel && (
          <p className="mb-3 flex items-start gap-1.5 text-xs text-codialis-gray">
            <BellRing className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="min-w-0">{texteRappel(seance.rappel)}</span>
          </p>
        )}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display font-bold text-codialis-navy">
            Émargements
            <span className="ml-2 text-sm font-normal text-codialis-gray">
              {cloturee
                ? '· feuille clôturée'
                : onRequalifier
                  ? '· cliquez un statut pour le requalifier'
                  : distance
                    ? '· émargez dans la classe, caméra allumée'
                    : '· signez votre présence'}
            </span>
          </h3>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-codialis-mint" /> Présent (
              {seance.compteurs.presents})
            </span>
            <span className="flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-amber-500" /> Retard (
              {seance.compteurs.retards})
            </span>
            <span className="flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-red-500" /> Absent (
              {seance.compteurs.absents})
            </span>
          </div>
        </div>

        <div className="space-y-2">
          {seance.presences.length === 0 && (
            <p className="py-6 text-center text-sm text-codialis-gray">
              Aucun apprenant convoqué à cette séance.
            </p>
          )}
          {seance.presences.map((presence) => (
            <div
              key={presence.id}
              className="flex flex-wrap items-center gap-3 rounded-xl bg-gray-50 px-3 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-codialis-navy">
                  {presence.apprenant}
                </div>
                <div className="text-[11px] text-codialis-gray">
                  {presence.signeLe
                    ? `Émargé${distance ? ' dans la salle' : ''} à ${heure(presence.signeLe)}`
                    : 'Pas encore émargé'}
                  {presence.justifiee
                    ? ` · justifiée${
                        presence.motifJustification ? ` : ${presence.motifJustification}` : ''
                      }`
                    : ''}
                </div>
                {presence.releve && (
                  <div className="flex items-start gap-1 text-[11px] text-codialis-gray">
                    <Video className="mt-px h-3 w-3 shrink-0" aria-hidden />
                    <span className="min-w-0">{presence.releve.resume}</span>
                  </div>
                )}
              </div>

              {presence.demandeEnAttente && <Pastille texte="Correction demandée" ton="warning" />}
              <Pastille texte={presence.statutLibelle} ton={presence.statutTon} />

              {cloturee && onDetail && (
                <button
                  type="button"
                  onClick={() => onDetail(presence.id)}
                  aria-label={`Détail de ${presence.apprenant}`}
                  className="min-h-[36px] rounded-lg border border-gray-200 bg-white px-2 py-1 text-[11px] font-semibold text-codialis-navy transition hover:bg-gray-50 [@media(pointer:coarse)]:min-h-[44px]"
                >
                  Détail
                </button>
              )}

              {!cloturee && (
                <div className="flex flex-wrap gap-1">
                  {/* Requalifier est un geste de formateur : l'apprenant qui
                      relit sa propre feuille n'a que le pavé de signature. */}
                  {onRequalifier && (
                    <>
                      {(['PRESENT', 'RETARD', 'ABSENT'] as const).map((statut) => (
                        <button
                          key={statut}
                          type="button"
                          onClick={() => onRequalifier(presence.id, statut)}
                          disabled={enCours}
                          className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-[11px] font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:opacity-50"
                        >
                          {statut === 'PRESENT'
                            ? 'Présent'
                            : statut === 'RETARD'
                              ? 'Retard'
                              : 'Absent'}
                        </button>
                      ))}
                      {presence.statut === 'ABSENT' && !presence.justifiee && (
                        <button
                          type="button"
                          onClick={() =>
                            onRequalifier(presence.id, 'ABSENT', true, 'Justificatif fourni')
                          }
                          disabled={enCours}
                          className="rounded-lg border border-codialis-mint bg-codialis-sand px-2 py-1 text-[11px] font-semibold text-codialis-navy disabled:opacity-50"
                        >
                          Justifier
                        </button>
                      )}
                    </>
                  )}
                  {!presence.signeLe && distance && !onRequalifier && (
                    <Link
                      to="/player"
                      className="inline-flex items-center rounded-lg bg-codialis-navy px-2 py-1 text-[11px] font-semibold text-white [@media(pointer:coarse)]:min-h-[40px]"
                    >
                      Émarger depuis la classe
                    </Link>
                  )}
                  {!presence.signeLe && !distance && (
                    <button
                      type="button"
                      onClick={() => setASigner(presence)}
                      disabled={enCours}
                      className="rounded-lg bg-codialis-navy px-2 py-1 text-[11px] font-semibold text-white disabled:opacity-50"
                    >
                      {onRequalifier ? 'Faire signer' : 'Signer'}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {aSigner && !cloturee && !distance && (
          <SignatureModal
            titre={`Signature · ${aSigner.apprenant}`}
            sousTitre={`${seance.libelle} · ${seance.demiJourneeLibelle} · ${seance.heureDebut}`}
            enCours={enCours}
            onAnnuler={() => setASigner(null)}
            onValider={(trace) => {
              onSigner(aSigner.id, trace);
              setASigner(null);
            }}
          />
        )}

        {!cloturee && onSignerFormateur && onCloturer && (
          <div className="mt-5 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {seance.formateurSigneLe ? (
                <p className="text-xs text-codialis-navy">
                  Feuille signée pour le formateur
                  {seance.formateurSignePar ? ` par ${seance.formateurSignePar}` : ''} à{' '}
                  {heure(seance.formateurSigneLe)}.
                </p>
              ) : distance ? (
                <p className="text-xs text-codialis-gray">
                  Classe à distance : le formateur l’atteste dans la salle e-learning.
                </p>
              ) : (
                <BoutonSecondaire onClick={() => setSignatureFormateur(true)} disabled={enCours}>
                  <PenLine className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  Signature du formateur
                </BoutonSecondaire>
              )}
            </div>
            <button
              type="button"
              onClick={onCloturer}
              disabled={enCours}
              className="w-full rounded-xl bg-codialis-mint py-3 text-sm font-semibold text-codialis-navy transition hover:bg-codialis-teal disabled:opacity-50"
            >
              Clôturer la séance et générer le PV
            </button>
            <p className="text-[11px] text-codialis-gray">
              À la clôture, les apprenants non émargés sont portés absents et la feuille
              n’est plus modifiable.
            </p>
          </div>
        )}

        {cloturee && (
          <div className="mt-5 space-y-2 rounded-xl bg-codialis-mint/10 p-4 text-xs text-codialis-navy">
            <p>Feuille clôturée le {heure(seance.clotureeLe)}. PV archivé.</p>
            {/* Le PV nomme toute la demi-journée : il se lit par l'équipe, pas
                depuis la vue de l'apprenant. */}
            {onCloturer && <LiensPv seance={seance} />}
          </div>
        )}

        {onSignerFormateur && !distance && (
          <ModaleSignatureFormateur
            seance={seance}
            ouverte={signatureFormateur && !seance.formateurSigneLe}
            enCours={enCours}
            erreur={erreurSignatureFormateur}
            onFermer={() => setSignatureFormateur(false)}
            onSigner={onSignerFormateur}
          />
        )}
      </div>
    </div>
  );
}
